import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ModalDialogBody } from '~/components/ModalDialog/components/ModalDialogBody';
import { ModalDialogClose } from '~/components/ModalDialog/components/ModalDialogClose';
import { ModalDialogContent } from '~/components/ModalDialog/components/ModalDialogContent';
import { ModalDialogFooter } from '~/components/ModalDialog/components/ModalDialogFooter';
import { ModalDialogHeader } from '~/components/ModalDialog/components/ModalDialogHeader';
import { ModalDialogTrigger } from '~/components/ModalDialog/components/ModalDialogTrigger';
import {
  CONTENT_CLASS,
  OVERLAY_CLASS,
} from '~/components/ModalDialog/components/styles';
import { renderSlot } from '~/components/ModalDialog/utils/renderSlot';

export interface ModalDialogProps extends Dialog.DialogProps {
  className?: string;
}

export function ModalDialog({
  children,
  className,
  ...dialog
}: ModalDialogProps) {
  const [isOpen, setIsOpen] = useState(
    dialog?.open || dialog?.defaultOpen || false,
  );
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dialog.open !== undefined) {
      setIsOpen(dialog.open);
    }
  }, [dialog.open]);

  const toggleIsOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      dialog.onOpenChange?.(open);
    },
    [dialog],
  );

  const closeModalDialog = useCallback(() => {
    toggleIsOpen(false);
  }, [toggleIsOpen]);

  const handleClose = useCallback(() => {
    overlayRef.current?.classList.remove(OVERLAY_CLASS.show);
    contentRef.current?.classList.remove(CONTENT_CLASS.show);

    contentRef.current?.addEventListener('animationend', closeModalDialog, {
      once: true,
    });

    requestAnimationFrame(() => {
      // Animate the overlay and dialog out of view as they close.
      overlayRef.current?.classList.add(OVERLAY_CLASS.hide);
      contentRef.current?.classList.add(
        CONTENT_CLASS.disable,
        CONTENT_CLASS.hide,
      );
    });
  }, [overlayRef, contentRef, closeModalDialog]);

  useEffect(() => {
    const dialogContent = contentRef.current;

    return () => {
      if (!isOpen) {
        dialogContent?.removeEventListener('animationend', closeModalDialog);
      }
    };
  }, [contentRef, isOpen, closeModalDialog]);

  const handleOpenChange = useCallback(
    (open: boolean) => (open ? toggleIsOpen(true) : handleClose()),
    [handleClose, toggleIsOpen],
  );

  return (
    <Dialog.Root {...dialog} open={isOpen} onOpenChange={handleOpenChange}>
      {renderSlot(ModalDialog.Trigger, children)}
      <ModalDialogContent
        overlayRef={overlayRef}
        contentRef={contentRef}
        className={className}
      >
        {children}
      </ModalDialogContent>
    </Dialog.Root>
  );
}

ModalDialog.displayName = 'ModalDialog';

ModalDialog.Trigger = ModalDialogTrigger;
ModalDialog.Header = ModalDialogHeader;
ModalDialog.Body = ModalDialogBody;
ModalDialog.Footer = ModalDialogFooter;
ModalDialog.Close = ModalDialogClose;
